/**
 * Adaptador de Persistencia Unificada Offline.
 *
 * Proporciona una interfaz desacoplada para almacenamiento local que soporta:
 * - Driver IndexedDB (Web / PWA y entorno por defecto).
 * - Driver SQLite (Contrato estructurado para Capacitor / Mobile nativo con DDL formal y codecs bidireccionales).
 * - Driver InMemory (Fallback resiliente en entornos degradados, SSR o testing).
 *
 * Gobernanza:
 * - Cero dependencias externas adicionales.
 * - Sin emojis.
 * - Identificadores y contratos en camelCase/PascalCase.
 */

import { Capacitor } from "@capacitor/core";
import { CapacitorSQLite } from "@capacitor-community/sqlite";

export const OFFLINE_STORES = {
    ENTRIES: "visita_offline_entries",
    OPERATIONS: "visita_offline_operations",
    MEDIA: "visita_offline_media",
    GPS: "offline_gps_points",
};

export const SQLITE_SCHEMAS = {
    [OFFLINE_STORES.ENTRIES]: `
        CREATE TABLE IF NOT EXISTS visita_offline_entries (
            key TEXT PRIMARY KEY NOT NULL,
            user_id INTEGER,
            visita_id INTEGER NOT NULL,
            revision_id TEXT NOT NULL,
            payload TEXT NOT NULL,
            updated_at TEXT NOT NULL
        );
        CREATE INDEX IF NOT EXISTS idx_entries_user ON visita_offline_entries(user_id);
        CREATE INDEX IF NOT EXISTS idx_entries_visita ON visita_offline_entries(visita_id);
    `,
    [OFFLINE_STORES.OPERATIONS]: `
        CREATE TABLE IF NOT EXISTS visita_offline_operations (
            id TEXT PRIMARY KEY NOT NULL,
            user_id INTEGER,
            visita_id INTEGER NOT NULL,
            type TEXT NOT NULL,
            operation_uuid TEXT,
            payload TEXT NOT NULL,
            status TEXT NOT NULL,
            retry_count INTEGER NOT NULL DEFAULT 0,
            in_flight_until TEXT,
            in_flight_at TEXT,
            owner_id TEXT,
            claim_token TEXT,
            last_error TEXT,
            dependencies TEXT,
            created_at TEXT NOT NULL,
            updated_at TEXT NOT NULL
        );
        CREATE INDEX IF NOT EXISTS idx_ops_user_status ON visita_offline_operations(user_id, status);
        CREATE INDEX IF NOT EXISTS idx_ops_visita ON visita_offline_operations(visita_id);
        CREATE INDEX IF NOT EXISTS idx_ops_op_uuid ON visita_offline_operations(operation_uuid);
    `,
    [OFFLINE_STORES.MEDIA]: `
        CREATE TABLE IF NOT EXISTS visita_offline_media (
            id TEXT PRIMARY KEY NOT NULL,
            user_id INTEGER,
            visita_id INTEGER NOT NULL,
            filename TEXT,
            mime_type TEXT,
            size INTEGER,
            descripcion TEXT,
            metadata TEXT,
            file_path TEXT,
            created_at TEXT NOT NULL,
            updated_at TEXT NOT NULL
        );
        CREATE INDEX IF NOT EXISTS idx_media_user ON visita_offline_media(user_id);
        CREATE INDEX IF NOT EXISTS idx_media_visita ON visita_offline_media(visita_id);
    `,
    [OFFLINE_STORES.GPS]: `
        CREATE TABLE IF NOT EXISTS offline_gps_points (
            id TEXT PRIMARY KEY NOT NULL,
            user_id INTEGER,
            visita_id INTEGER,
            latitude REAL NOT NULL,
            longitude REAL NOT NULL,
            accuracy REAL,
            battery_level INTEGER,
            timestamp TEXT NOT NULL,
            status TEXT NOT NULL DEFAULT 'pending',
            retry_count INTEGER NOT NULL DEFAULT 0,
            created_at TEXT NOT NULL,
            updated_at TEXT NOT NULL
        );
        CREATE INDEX IF NOT EXISTS idx_gps_user_status ON offline_gps_points(user_id, status);
        CREATE INDEX IF NOT EXISTS idx_gps_timestamp ON offline_gps_points(timestamp);
    `,
};

export function getPrimaryKeyName(storeName) {
    if (storeName === OFFLINE_STORES.ENTRIES) {
        return "key";
    }
    return "id";
}

function safeJsonStringify(value, fallback = "{}") {
    if (value === undefined || value === null) {
        return fallback;
    }
    try {
        return JSON.stringify(value);
    } catch {
        return fallback;
    }
}

function safeJsonParse(value, fallback = {}) {
    if (typeof value !== "string" || !value.trim()) {
        return fallback;
    }
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
}

export function encodeRowForSqlite(storeName, item) {
    if (!item || typeof item !== "object") {
        return {};
    }

    if (storeName === OFFLINE_STORES.ENTRIES) {
        const userId = item.userId !== undefined ? item.userId : item.user_id;
        const visitaId = item.visitaId !== undefined ? item.visitaId : item.visita_id;
        const revisionId = item.revisionId !== undefined ? item.revisionId : item.revision_id;
        const updatedAt = item.updatedAt !== undefined ? item.updatedAt : item.updated_at;

        return {
            key: String(item.key || ""),
            user_id: userId !== undefined && userId !== null ? Number(userId) : null,
            visita_id: Number(visitaId || 0),
            revision_id: String(revisionId || ""),
            payload: typeof item.payload === "string" ? item.payload : safeJsonStringify(item.payload, "{}"),
            updated_at: String(updatedAt || new Date().toISOString()),
        };
    }

    if (storeName === OFFLINE_STORES.OPERATIONS) {
        const userId = item.userId !== undefined ? item.userId : item.user_id;
        const visitaId = item.visitaId !== undefined ? item.visitaId : item.visita_id;
        const opUuid = item.operationUuid !== undefined ? item.operationUuid : item.operation_uuid;
        const retryCount = item.retryCount !== undefined ? item.retryCount : item.retry_count;
        const inFlightUntil = item.inFlightUntil !== undefined
            ? item.inFlightUntil
            : (item.in_flight_until !== undefined
                ? item.in_flight_until
                : (item.leaseExpiresAt !== undefined ? item.leaseExpiresAt : item.lease_expires_at));
        const inFlightAt = item.inFlightAt !== undefined ? item.inFlightAt : item.in_flight_at;
        const ownerId = item.ownerId !== undefined ? item.ownerId : item.owner_id;
        const claimToken = item.claimToken !== undefined ? item.claimToken : item.claim_token;
        const lastError = item.lastError !== undefined ? item.lastError : item.last_error;
        const createdAt = item.createdAt !== undefined ? item.createdAt : item.created_at;
        const updatedAt = item.updatedAt !== undefined ? item.updatedAt : item.updated_at;

        return {
            id: String(item.id || ""),
            user_id: userId !== undefined && userId !== null ? Number(userId) : null,
            visita_id: Number(visitaId || 0),
            type: String(item.type || ""),
            operation_uuid: opUuid ? String(opUuid) : null,
            payload: typeof item.payload === "string" ? item.payload : safeJsonStringify(item.payload, "{}"),
            status: String(item.status || "pending"),
            retry_count: Number(retryCount || 0),
            in_flight_until: inFlightUntil ? String(inFlightUntil) : null,
            in_flight_at: inFlightAt ? String(inFlightAt) : null,
            owner_id: ownerId ? String(ownerId) : null,
            claim_token: claimToken ? String(claimToken) : null,
            last_error: lastError ? String(lastError) : null,
            dependencies: safeJsonStringify(Array.isArray(item.dependencies) ? item.dependencies : safeJsonParse(item.dependencies, []), "[]"),
            created_at: String(createdAt || new Date().toISOString()),
            updated_at: String(updatedAt || new Date().toISOString()),
        };
    }

    if (storeName === OFFLINE_STORES.MEDIA) {
        const userId = item.userId !== undefined ? item.userId : item.user_id;
        const visitaId = item.visitaId !== undefined ? item.visitaId : item.visita_id;
        const mimeType = item.mimeType !== undefined ? item.mimeType : item.mime_type;
        const filePath = item.filePath !== undefined ? item.filePath : item.file_path;
        const createdAt = item.createdAt !== undefined ? item.createdAt : item.created_at;
        const updatedAt = item.updatedAt !== undefined ? item.updatedAt : item.updated_at;

        return {
            id: String(item.id || ""),
            user_id: userId !== undefined && userId !== null ? Number(userId) : null,
            visita_id: Number(visitaId || 0),
            filename: item.filename ? String(item.filename) : null,
            mime_type: mimeType ? String(mimeType) : null,
            size: Number(item.size || 0),
            descripcion: item.descripcion ? String(item.descripcion) : null,
            metadata: safeJsonStringify(item.metadata, "{}"),
            file_path: filePath ? String(filePath) : null,
            created_at: String(createdAt || new Date().toISOString()),
            updated_at: String(updatedAt || new Date().toISOString()),
        };
    }

    if (storeName === OFFLINE_STORES.GPS) {
        const userId = item.userId !== undefined ? item.userId : item.user_id;
        const visitaId = item.visitaId !== undefined ? item.visitaId : item.visita_id;
        const batteryLevel = item.batteryLevel !== undefined ? item.batteryLevel : item.battery_level;
        const retryCount = item.retryCount !== undefined ? item.retryCount : item.retry_count;
        const createdAt = item.createdAt !== undefined ? item.createdAt : item.created_at;
        const updatedAt = item.updatedAt !== undefined ? item.updatedAt : item.updated_at;

        return {
            id: String(item.id || item.uuid || ""),
            user_id: userId !== undefined && userId !== null ? Number(userId) : null,
            visita_id: visitaId !== undefined && visitaId !== null ? Number(visitaId) : null,
            latitude: Number(item.latitude || 0),
            longitude: Number(item.longitude || 0),
            accuracy: item.accuracy != null ? Number(item.accuracy) : null,
            battery_level: batteryLevel != null ? Number(batteryLevel) : null,
            timestamp: String(item.timestamp || new Date().toISOString()),
            status: String(item.status || "pending"),
            retry_count: Number(retryCount || 0),
            created_at: String(createdAt || new Date().toISOString()),
            updated_at: String(updatedAt || new Date().toISOString()),
        };
    }

    return { ...item };
}

export function decodeRowFromSqlite(storeName, row) {
    if (!row || typeof row !== "object") {
        return null;
    }

    if (storeName === OFFLINE_STORES.ENTRIES) {
        return {
            key: row.key,
            userId: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            user_id: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            visitaId: Number(row.visita_id || 0),
            visita_id: Number(row.visita_id || 0),
            revisionId: row.revision_id,
            revision_id: row.revision_id,
            payload: safeJsonParse(row.payload, {}),
            updatedAt: row.updated_at,
            updated_at: row.updated_at,
        };
    }

    if (storeName === OFFLINE_STORES.OPERATIONS) {
        return {
            id: row.id,
            userId: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            user_id: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            visitaId: Number(row.visita_id || 0),
            visita_id: Number(row.visita_id || 0),
            type: row.type,
            operationUuid: row.operation_uuid || null,
            operation_uuid: row.operation_uuid || null,
            payload: safeJsonParse(row.payload, {}),
            status: row.status,
            retryCount: Number(row.retry_count || 0),
            retry_count: Number(row.retry_count || 0),
            inFlightUntil: row.in_flight_until || null,
            in_flight_until: row.in_flight_until || null,
            leaseExpiresAt: row.in_flight_until !== null && row.in_flight_until !== undefined ? Number(row.in_flight_until) : null,
            lease_expires_at: row.in_flight_until !== null && row.in_flight_until !== undefined ? Number(row.in_flight_until) : null,
            inFlightAt: row.in_flight_at || null,
            in_flight_at: row.in_flight_at || null,
            ownerId: row.owner_id || null,
            owner_id: row.owner_id || null,
            claimToken: row.claim_token || null,
            claim_token: row.claim_token || null,
            lastError: row.last_error || null,
            last_error: row.last_error || null,
            dependencies: safeJsonParse(row.dependencies, []),
            createdAt: row.created_at,
            created_at: row.created_at,
            updatedAt: row.updated_at,
            updated_at: row.updated_at,
        };
    }

    if (storeName === OFFLINE_STORES.MEDIA) {
        return {
            id: row.id,
            userId: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            user_id: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            visitaId: Number(row.visita_id || 0),
            visita_id: Number(row.visita_id || 0),
            filename: row.filename || null,
            mimeType: row.mime_type || null,
            mime_type: row.mime_type || null,
            size: Number(row.size || 0),
            descripcion: row.descripcion || "",
            metadata: safeJsonParse(row.metadata, {}),
            filePath: row.file_path || null,
            file_path: row.file_path || null,
            createdAt: row.created_at,
            created_at: row.created_at,
            updatedAt: row.updated_at,
            updated_at: row.updated_at,
        };
    }

    if (storeName === OFFLINE_STORES.GPS) {
        return {
            id: row.id,
            uuid: row.id,
            userId: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            user_id: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            visitaId: row.visita_id !== null && row.visita_id !== undefined ? Number(row.visita_id) : null,
            visita_id: row.visita_id !== null && row.visita_id !== undefined ? Number(row.visita_id) : null,
            latitude: Number(row.latitude || 0),
            longitude: Number(row.longitude || 0),
            accuracy: row.accuracy != null ? Number(row.accuracy) : null,
            batteryLevel: row.battery_level != null ? Number(row.battery_level) : null,
            battery_level: row.battery_level != null ? Number(row.battery_level) : null,
            timestamp: row.timestamp,
            status: row.status || "pending",
            retryCount: Number(row.retry_count || 0),
            retry_count: Number(row.retry_count || 0),
            createdAt: row.created_at,
            created_at: row.created_at,
            updatedAt: row.updated_at,
            updated_at: row.updated_at,
        };
    }

    return { ...row };
}

/**
 * Driver IndexedDB para PWA y navegadores modernos.
 * Garantiza durabilidad transaccional escuchando tx.oncomplete.
 */
class IndexedDBStorageDriver {
    constructor(dbName = "coordinacion_visitas_offline", version = 3) {
        this.dbName = dbName;
        this.version = version;
        this.db = null;
        this.initPromise = null;
    }

    async init() {
        if (this.db) {
            return this.db;
        }

        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = new Promise((resolve, reject) => {
            if (typeof window === "undefined" || !window.indexedDB) {
                reject(new Error("IndexedDB no disponible en este entorno."));
                return;
            }

            const request = window.indexedDB.open(this.dbName, this.version);

            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                if (!db.objectStoreNames.contains(OFFLINE_STORES.ENTRIES)) {
                    const store = db.createObjectStore(OFFLINE_STORES.ENTRIES, { keyPath: "key" });
                    store.createIndex("userId", "userId", { unique: false });
                    store.createIndex("visitaId", "visitaId", { unique: false });
                    store.createIndex("by_user", "userId", { unique: false });
                    store.createIndex("by_updated_at", "updatedAt", { unique: false });
                }

                if (!db.objectStoreNames.contains(OFFLINE_STORES.OPERATIONS)) {
                    const store = db.createObjectStore(OFFLINE_STORES.OPERATIONS, { keyPath: "id" });
                    store.createIndex("userId", "userId", { unique: false });
                    store.createIndex("visitaId", "visitaId", { unique: false });
                    store.createIndex("status", "status", { unique: false });
                    store.createIndex("by_user", "userId", { unique: false });
                    store.createIndex("by_visita", "visitaId", { unique: false });
                    store.createIndex("by_status", "status", { unique: false });
                }

                if (!db.objectStoreNames.contains(OFFLINE_STORES.MEDIA)) {
                    const store = db.createObjectStore(OFFLINE_STORES.MEDIA, { keyPath: "id" });
                    store.createIndex("userId", "userId", { unique: false });
                    store.createIndex("visitaId", "visitaId", { unique: false });
                    store.createIndex("status", "status", { unique: false });
                    store.createIndex("by_user", "userId", { unique: false });
                    store.createIndex("by_visita", "visitaId", { unique: false });
                }

                if (!db.objectStoreNames.contains(OFFLINE_STORES.GPS)) {
                    const store = db.createObjectStore(OFFLINE_STORES.GPS, { keyPath: "id" });
                    store.createIndex("userId", "userId", { unique: false });
                    store.createIndex("status", "status", { unique: false });
                    store.createIndex("timestamp", "timestamp", { unique: false });
                }
            };

            request.onsuccess = () => {
                this.db = request.result;
                resolve(this.db);
            };

            request.onerror = () => {
                reject(request.error || new Error("Error abriendo base de datos IndexedDB."));
            };
        });

        return this.initPromise;
    }

    async get(storeName, key) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, "readonly");
            const store = tx.objectStore(storeName);
            const req = store.get(key);
            req.onsuccess = () => resolve(req.result || null);
            req.onerror = () => reject(req.error);
        });
    }

    async getAll(storeName, filterFn = null) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, "readonly");
            const store = tx.objectStore(storeName);
            const req = store.getAll();
            req.onsuccess = () => {
                const results = req.result || [];
                resolve(typeof filterFn === "function" ? results.filter(filterFn) : results);
            };
            req.onerror = () => reject(req.error);
        });
    }

    async put(storeName, value) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, "readwrite");
            const store = tx.objectStore(storeName);
            store.put(value);
            tx.oncomplete = () => resolve(value);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error || new Error("Transacción abortada en IndexedDB."));
        });
    }

    async atomicPutBatch(items) {
        if (!Array.isArray(items) || items.length === 0) {
            return [];
        }

        const db = await this.init();
        const storeNames = Array.from(new Set(items.map((i) => i.storeName)));

        return new Promise((resolve, reject) => {
            try {
                const tx = db.transaction(storeNames, "readwrite");

                tx.oncomplete = () => {
                    resolve(items.map((i) => i.value));
                };

                tx.onerror = () => {
                    reject(tx.error || new Error("Error en atomicPutBatch de IndexedDB."));
                };

                tx.onabort = () => {
                    reject(tx.error || new Error("Transacción abortada en atomicPutBatch de IndexedDB."));
                };

                for (const item of items) {
                    const store = tx.objectStore(item.storeName);
                    store.put(item.value);
                }
            } catch (error) {
                reject(error);
            }
        });
    }

    async mutate(storeName, key, mutatorFn) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, "readwrite");
            const store = tx.objectStore(storeName);
            const getReq = store.get(key);
            let resultValue = null;

            getReq.onsuccess = () => {
                const current = getReq.result || null;
                const next = mutatorFn(current);
                if (next === null || next === undefined) {
                    resultValue = null;
                    return;
                }
                resultValue = next;
                store.put(next);
            };

            getReq.onerror = () => reject(getReq.error);
            tx.oncomplete = () => resolve(resultValue);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error || new Error("Transacción abortada en mutate de IndexedDB."));
        });
    }

    async compareAndDelete(storeName, key, predicateFn) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, "readwrite");
            const store = tx.objectStore(storeName);
            const getReq = store.get(key);
            let deleted = false;

            getReq.onsuccess = () => {
                const current = getReq.result || null;
                if (!current) {
                    deleted = false;
                    return;
                }
                if (typeof predicateFn === "function" && !predicateFn(current)) {
                    deleted = false;
                    return;
                }
                store.delete(key);
                deleted = true;
            };

            getReq.onerror = () => reject(getReq.error);
            tx.oncomplete = () => resolve(deleted);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error || new Error("Transacción abortada en compareAndDelete de IndexedDB."));
        });
    }

    async delete(storeName, key) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, "readwrite");
            const store = tx.objectStore(storeName);
            store.delete(key);
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error || new Error("Transacción abortada en IndexedDB."));
        });
    }

    async clear(storeName) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, "readwrite");
            const store = tx.objectStore(storeName);
            store.clear();
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error || new Error("Transacción abortada en IndexedDB."));
        });
    }

    async count(storeName, filterFn = null) {
        if (!filterFn) {
            const db = await this.init();
            return new Promise((resolve, reject) => {
                const tx = db.transaction(storeName, "readonly");
                const store = tx.objectStore(storeName);
                const req = store.count();
                req.onsuccess = () => resolve(req.result || 0);
                req.onerror = () => reject(req.error);
            });
        }
        const items = await this.getAll(storeName, filterFn);
        return items.length;
    }
}

/**
 * Fábrica del conector SQLite nativo estándar basado en @capacitor-community/sqlite.
 * Permite ejecutar transacciones, consultas, DDL y verificaciones sobre la base local.
 *
 * @param {string} [databaseName="coordinacion_visitas_offline"]
 * @returns {object} Conector compatible con SQLiteStorageDriver
 */
export function createDefaultCapacitorSqliteConnector(databaseName = "coordinacion_visitas_offline") {
    let isOpen = false;

    return {
        databaseName,
        async init() {
            if (isOpen) {
                return;
            }
            try {
                const check = await CapacitorSQLite.isDBOpen({ database: databaseName });
                if (check?.result) {
                    isOpen = true;
                    return;
                }
            } catch {
                // Si la conexión no existe aún en el plugin, fallará isDBOpen
            }

            try {
                await CapacitorSQLite.createConnection({
                    database: databaseName,
                    version: 1,
                    encrypted: false,
                    mode: "no-encryption",
                    readonly: false,
                });
            } catch {
                // Si la conexión ya fue creada previamente en memoria nativa del plugin
            }

            await CapacitorSQLite.open({ database: databaseName, readonly: false });
            isOpen = true;
        },
        async execute(statements) {
            await this.init();
            return CapacitorSQLite.execute({ database: databaseName, statements });
        },
        async query(statement, values = []) {
            await this.init();
            return CapacitorSQLite.query({ database: databaseName, statement, values });
        },
        async run(statement, values = []) {
            await this.init();
            return CapacitorSQLite.run({ database: databaseName, statement, values });
        },
        async executeSet(set) {
            await this.init();
            return CapacitorSQLite.executeSet({ database: databaseName, set });
        },
        async beginTransaction() {
            await this.init();
            if (typeof CapacitorSQLite.beginTransaction === "function") {
                return CapacitorSQLite.beginTransaction({ database: databaseName });
            }
        },
        async commitTransaction() {
            await this.init();
            if (typeof CapacitorSQLite.commitTransaction === "function") {
                return CapacitorSQLite.commitTransaction({ database: databaseName });
            }
        },
        async rollbackTransaction() {
            await this.init();
            if (typeof CapacitorSQLite.rollbackTransaction === "function") {
                return CapacitorSQLite.rollbackTransaction({ database: databaseName });
            }
        },
        async close() {
            if (!isOpen) return;
            try {
                await CapacitorSQLite.closeConnection({ database: databaseName, readonly: false });
            } finally {
                isOpen = false;
            }
        },
    };
}

/**
 * Driver SQLite estructurado para Capacitor Nativo.
 * Ejecuta consultas SQLite con DDL estricto y codecs de datos.
 * Evita la división de datos (split-brain) por operación: si SQLite está activo, opera exclusivamente sobre SQLite.
 * Si falla la inicialización nativa y no opera en modo estricto, degrada de forma uniforme y audible a fallbackDriver.
 */
class SQLiteStorageDriver {
    constructor(nativeConnector = null, fallbackDriver = null, options = {}) {
        this.nativeConnector = nativeConnector;
        this.fallbackDriver = fallbackDriver || new IndexedDBStorageDriver();
        this.isInitialized = false;
        this.strict = Boolean(options.strict);
        this.effectiveEngine = "uninitialized";
        this.isFallback = false;
        this.lastInitError = null;
    }

    async init() {
        if (this.isInitialized) {
            return;
        }

        const isNative = typeof Capacitor !== "undefined" && typeof Capacitor.isNativePlatform === "function" && Capacitor.isNativePlatform();

        // Si no se proporcionó conector explícito pero estamos en plataforma nativa, instanciamos el conector real
        if (!this.nativeConnector && isNative) {
            try {
                this.nativeConnector = createDefaultCapacitorSqliteConnector();
            } catch (connectorError) {
                this.lastInitError = connectorError;
                console.warn("[SQLiteStorageDriver] No se pudo crear el conector SQLite nativo por defecto:", connectorError);
            }
        }

        if (this.nativeConnector) {
            try {
                if (typeof this.nativeConnector.init === "function") {
                    await this.nativeConnector.init();
                }
                if (typeof this.nativeConnector.execute === "function") {
                    await this.nativeConnector.execute(SQLITE_SCHEMAS[OFFLINE_STORES.ENTRIES]);
                    await this.nativeConnector.execute(SQLITE_SCHEMAS[OFFLINE_STORES.OPERATIONS]);
                    await this.nativeConnector.execute(SQLITE_SCHEMAS[OFFLINE_STORES.MEDIA]);
                    await this.nativeConnector.execute(SQLITE_SCHEMAS[OFFLINE_STORES.GPS]);
                    await this._migrateExistingTables();
                    this.effectiveEngine = "sqlite";
                    this.isFallback = false;
                    this.isInitialized = true;
                    return;
                }
            } catch (err) {
                this.lastInitError = err;
                console.warn("[SQLiteStorageDriver] Falló la inicialización del conector SQLite nativo:", err);
                if (this.strict) {
                    throw new Error(`[SQLiteStorageDriver] Error fatal al inicializar SQLite nativo en modo estricto: ${err?.message || err}`);
                }
            }
        }

        if (this.strict) {
            throw new Error("[SQLiteStorageDriver] Conector SQLite nativo no disponible en modo estricto.");
        }

        if (isNative) {
            console.warn(
                "[SQLiteStorageDriver] ADVERTENCIA CRITICA: Entorno nativo Android ejecutando con IndexedDB como fallback. SQLite no está activo."
            );
        }

        await this.fallbackDriver.init();
        this.effectiveEngine = "indexeddb";
        this.isFallback = true;
        this.isInitialized = true;
    }

    async _migrateExistingTables() {
        if (!this.nativeConnector || typeof this.nativeConnector.query !== "function") {
            return;
        }

        const expectedColumns = {
            [OFFLINE_STORES.ENTRIES]: [
                { name: "revision_id", type: "TEXT DEFAULT ''" },
                { name: "payload", type: "TEXT DEFAULT '{}'" },
                { name: "updated_at", type: "TEXT DEFAULT ''" },
            ],
            [OFFLINE_STORES.OPERATIONS]: [
                { name: "operation_uuid", type: "TEXT" },
                { name: "retry_count", type: "INTEGER NOT NULL DEFAULT 0" },
                { name: "in_flight_until", type: "TEXT" },
                { name: "in_flight_at", type: "TEXT" },
                { name: "owner_id", type: "TEXT" },
                { name: "claim_token", type: "TEXT" },
                { name: "last_error", type: "TEXT" },
                { name: "dependencies", type: "TEXT DEFAULT '[]'" },
                { name: "created_at", type: "TEXT DEFAULT ''" },
                { name: "updated_at", type: "TEXT DEFAULT ''" },
            ],
            [OFFLINE_STORES.MEDIA]: [
                { name: "filename", type: "TEXT" },
                { name: "mime_type", type: "TEXT" },
                { name: "size", type: "INTEGER NOT NULL DEFAULT 0" },
                { name: "descripcion", type: "TEXT" },
                { name: "metadata", type: "TEXT DEFAULT '{}'" },
                { name: "file_path", type: "TEXT" },
                { name: "created_at", type: "TEXT DEFAULT ''" },
                { name: "updated_at", type: "TEXT DEFAULT ''" },
            ],
            [OFFLINE_STORES.GPS]: [
                { name: "visita_id", type: "INTEGER" },
                { name: "accuracy", type: "REAL" },
                { name: "battery_level", type: "INTEGER" },
                { name: "status", type: "TEXT DEFAULT 'pending'" },
                { name: "retry_count", type: "INTEGER NOT NULL DEFAULT 0" },
                { name: "created_at", type: "TEXT DEFAULT ''" },
                { name: "updated_at", type: "TEXT DEFAULT ''" },
            ],
        };

        for (const [tableName, cols] of Object.entries(expectedColumns)) {
            try {
                const info = await this.nativeConnector.query(`PRAGMA table_info(${tableName});`);
                const existing = new Set((info?.values || []).map((c) => String(c.name || "").toLowerCase()));

                for (const col of cols) {
                    if (!existing.has(col.name.toLowerCase())) {
                        await this.nativeConnector.execute(`ALTER TABLE ${tableName} ADD COLUMN ${col.name} ${col.type};`);
                    }
                }
            } catch {
                // Silencioso si la tabla no soporta PRAGMA o es virtual
            }
        }
    }

    async _acquireKeyLock(lockKey) {
        if (!this._keyLocks) {
            this._keyLocks = new Map();
        }
        while (this._keyLocks.has(lockKey)) {
            await new Promise((resolve) => setTimeout(resolve, 5));
        }
        this._keyLocks.set(lockKey, true);
    }

    _releaseKeyLock(lockKey) {
        if (this._keyLocks) {
            this._keyLocks.delete(lockKey);
        }
    }

    async _withKeyLock(lockKey, taskFn) {
        await this._acquireKeyLock(lockKey);
        try {
            return await taskFn();
        } finally {
            this._releaseKeyLock(lockKey);
        }
    }

    async _withKeyLocks(lockKeys, taskFn) {
        const uniqueSorted = Array.from(new Set(lockKeys.filter(Boolean))).sort();
        for (const k of uniqueSorted) {
            await this._acquireKeyLock(k);
        }
        try {
            return await taskFn();
        } finally {
            for (const k of uniqueSorted.reverse()) {
                this._releaseKeyLock(k);
            }
        }
    }

    async get(storeName, key) {
        await this.init();
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && typeof this.nativeConnector.query === "function") {
            const pk = getPrimaryKeyName(storeName);
            const res = await this.nativeConnector.query(
                `SELECT * FROM ${storeName} WHERE ${pk} = ? LIMIT 1`,
                [key]
            );
            const row = res?.values?.[0] || null;
            return row ? decodeRowFromSqlite(storeName, row) : null;
        }
        return this.fallbackDriver.get(storeName, key);
    }

    async getAll(storeName, filterFn = null) {
        await this.init();
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && typeof this.nativeConnector.query === "function") {
            const res = await this.nativeConnector.query(`SELECT * FROM ${storeName}`);
            const rows = (res?.values || []).map((row) => decodeRowFromSqlite(storeName, row));
            return typeof filterFn === "function" ? rows.filter(filterFn) : rows;
        }
        return this.fallbackDriver.getAll(storeName, filterFn);
    }

    async put(storeName, value) {
        await this.init();
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && typeof this.nativeConnector.run === "function") {
            const pk = getPrimaryKeyName(storeName);
            const keyVal = String(value?.[pk] || "");
            const lockKey = `${storeName}:${keyVal}`;

            return this._withKeyLock(lockKey, async () => {
                const encoded = encodeRowForSqlite(storeName, value);
                const columns = Object.keys(encoded);
                const placeholders = columns.map(() => "?").join(", ");
                const values = Object.values(encoded);

                await this.nativeConnector.run(
                    `INSERT OR REPLACE INTO ${storeName} (${columns.join(", ")}) VALUES (${placeholders})`,
                    values
                );
                return value;
            });
        }
        return this.fallbackDriver.put(storeName, value);
    }

    async atomicPutBatch(items) {
        if (!Array.isArray(items) || items.length === 0) {
            return [];
        }

        await this.init();
        if (this.effectiveEngine === "sqlite" && this.nativeConnector) {
            const lockKeys = items.map((item) => {
                const pk = getPrimaryKeyName(item.storeName);
                const keyVal = String(item.value?.[pk] || "");
                return `${item.storeName}:${keyVal}`;
            });

            return this._withKeyLocks(lockKeys, async () => {
                if (typeof this.nativeConnector.executeSet === "function") {
                    const set = items.map((item) => {
                        const encoded = encodeRowForSqlite(item.storeName, item.value);
                        const columns = Object.keys(encoded);
                        const placeholders = columns.map(() => "?").join(", ");
                        const values = Object.values(encoded);
                        return {
                            statement: `INSERT OR REPLACE INTO ${item.storeName} (${columns.join(", ")}) VALUES (${placeholders})`,
                            values,
                        };
                    });
                    await this.nativeConnector.executeSet(set);
                    return items.map((i) => i.value);
                }

                if (typeof this.nativeConnector.run === "function") {
                    for (const item of items) {
                        const encoded = encodeRowForSqlite(item.storeName, item.value);
                        const columns = Object.keys(encoded);
                        const placeholders = columns.map(() => "?").join(", ");
                        const values = Object.values(encoded);
                        await this.nativeConnector.run(
                            `INSERT OR REPLACE INTO ${item.storeName} (${columns.join(", ")}) VALUES (${placeholders})`,
                            values
                        );
                    }
                    return items.map((i) => i.value);
                }
            });
        }

        return this.fallbackDriver.atomicPutBatch(items);
    }

    async mutate(storeName, key, mutatorFn) {
        await this.init();
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && typeof this.nativeConnector.run === "function") {
            const lockKey = `${storeName}:${key}`;
            return this._withKeyLock(lockKey, async () => {
                const current = await this.get(storeName, key);
                const next = mutatorFn(current);
                if (next !== null && next !== undefined) {
                    const encoded = encodeRowForSqlite(storeName, next);
                    const columns = Object.keys(encoded);
                    const placeholders = columns.map(() => "?").join(", ");
                    const values = Object.values(encoded);
                    await this.nativeConnector.run(
                        `INSERT OR REPLACE INTO ${storeName} (${columns.join(", ")}) VALUES (${placeholders})`,
                        values
                    );
                }
                return next;
            });
        }
        return this.fallbackDriver.mutate(storeName, key, mutatorFn);
    }

    async compareAndDelete(storeName, key, predicateFn) {
        await this.init();
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && typeof this.nativeConnector.run === "function") {
            const lockKey = `${storeName}:${key}`;
            return this._withKeyLock(lockKey, async () => {
                const current = await this.get(storeName, key);
                let deleted = false;
                if (current && (typeof predicateFn !== "function" || predicateFn(current))) {
                    const pk = getPrimaryKeyName(storeName);
                    // Fencing nativo a nivel sentencia SQL:
                    // Si current posee revision_id, se añade al predicado WHERE de forma que
                    // incluso si un hilo o proceso externo modificara la fila, SQLite rechaza el DELETE
                    if (storeName === OFFLINE_STORES.ENTRIES && current.revisionId) {
                        await this.nativeConnector.run(
                            `DELETE FROM ${storeName} WHERE ${pk} = ? AND revision_id = ?`,
                            [key, current.revisionId]
                        );
                    } else if (storeName === OFFLINE_STORES.OPERATIONS && current.claimToken) {
                        await this.nativeConnector.run(
                            `DELETE FROM ${storeName} WHERE ${pk} = ? AND claim_token = ?`,
                            [key, current.claimToken]
                        );
                    } else if (storeName === OFFLINE_STORES.OPERATIONS && !current.claimToken) {
                        await this.nativeConnector.run(
                            `DELETE FROM ${storeName} WHERE ${pk} = ? AND (claim_token IS NULL OR claim_token = '')`,
                            [key]
                        );
                    } else {
                        await this.nativeConnector.run(
                            `DELETE FROM ${storeName} WHERE ${pk} = ?`,
                            [key]
                        );
                    }
                    // Verificación determinística en base de datos:
                    const remaining = await this.get(storeName, key);
                    deleted = !remaining;
                }
                return deleted;
            });
        }
        return this.fallbackDriver.compareAndDelete(storeName, key, predicateFn);
    }

    async delete(storeName, key) {
        await this.init();
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && typeof this.nativeConnector.run === "function") {
            const lockKey = `${storeName}:${key}`;
            return this._withKeyLock(lockKey, async () => {
                const pk = getPrimaryKeyName(storeName);
                await this.nativeConnector.run(
                    `DELETE FROM ${storeName} WHERE ${pk} = ?`,
                    [key]
                );
                return true;
            });
        }
        return this.fallbackDriver.delete(storeName, key);
    }

    async clear(storeName) {
        await this.init();
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && typeof this.nativeConnector.run === "function") {
            await this.nativeConnector.run(`DELETE FROM ${storeName}`);
            return true;
        }
        return this.fallbackDriver.clear(storeName);
    }

    async count(storeName, filterFn = null) {
        await this.init();
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && !filterFn && typeof this.nativeConnector.query === "function") {
            const res = await this.nativeConnector.query(`SELECT COUNT(*) as total FROM ${storeName}`);
            return Number(res?.values?.[0]?.total || 0);
        }
        return this.fallbackDriver.count(storeName, filterFn);
    }
}

/**
 * Driver en memoria para testing y entornos aislados.
 */
class MemoryStorageDriver {
    constructor() {
        this.stores = new Map();
        this._locks = new Map();
    }

    _getStore(storeName) {
        if (!this.stores.has(storeName)) {
            this.stores.set(storeName, new Map());
        }
        return this.stores.get(storeName);
    }

    async _acquireLock(lockKey) {
        if (!this._locks) {
            this._locks = new Map();
        }
        while (this._locks.has(lockKey)) {
            await new Promise((resolve) => setTimeout(resolve, 5));
        }
        this._locks.set(lockKey, true);
    }

    _releaseLock(lockKey) {
        if (this._locks) {
            this._locks.delete(lockKey);
        }
    }

    async _withLock(lockKey, taskFn) {
        await this._acquireLock(lockKey);
        try {
            return await taskFn();
        } finally {
            this._releaseLock(lockKey);
        }
    }

    async _withLocks(lockKeys, taskFn) {
        const uniqueSorted = Array.from(new Set(lockKeys.filter(Boolean))).sort();
        for (const k of uniqueSorted) {
            await this._acquireLock(k);
        }
        try {
            return await taskFn();
        } finally {
            for (const k of uniqueSorted.reverse()) {
                this._releaseLock(k);
            }
        }
    }

    async init() {
        return true;
    }

    async get(storeName, key) {
        const item = this._getStore(storeName).get(String(key));
        return item ? { ...item } : null;
    }

    async getAll(storeName, filterFn = null) {
        const items = Array.from(this._getStore(storeName).values()).map((i) => ({ ...i }));
        return typeof filterFn === "function" ? items.filter(filterFn) : items;
    }

    async put(storeName, value) {
        const pk = getPrimaryKeyName(storeName);
        const key = String(value?.[pk] || "");
        const lockKey = `${storeName}:${key}`;

        return this._withLock(lockKey, async () => {
            this._getStore(storeName).set(key, { ...value });
            return value;
        });
    }

    async atomicPutBatch(items) {
        if (!Array.isArray(items) || items.length === 0) {
            return [];
        }
        for (const item of items) {
            if (!item || !item.storeName || !item.value) {
                throw new Error("Item inválido en atomicPutBatch de MemoryStorageDriver.");
            }
        }

        const lockKeys = items.map((item) => {
            const pk = getPrimaryKeyName(item.storeName);
            const key = String(item.value?.[pk] || "");
            return `${item.storeName}:${key}`;
        });

        return this._withLocks(lockKeys, async () => {
            for (const item of items) {
                const pk = getPrimaryKeyName(item.storeName);
                const key = String(item.value?.[pk] || "");
                this._getStore(item.storeName).set(key, { ...item.value });
            }
            return items.map((i) => i.value);
        });
    }

    async mutate(storeName, key, mutatorFn) {
        const lockKey = `${storeName}:${key}`;
        return this._withLock(lockKey, async () => {
            const current = await this.get(storeName, key);
            const next = mutatorFn(current);
            if (next !== null && next !== undefined) {
                const pk = getPrimaryKeyName(storeName);
                const keyStr = String(next?.[pk] || key);
                this._getStore(storeName).set(keyStr, { ...next });
            }
            return next;
        });
    }

    async compareAndDelete(storeName, key, predicateFn) {
        const lockKey = `${storeName}:${key}`;
        return this._withLock(lockKey, async () => {
            const current = await this.get(storeName, key);
            if (!current) return false;
            if (typeof predicateFn === "function" && !predicateFn(current)) {
                return false;
            }
            this._getStore(storeName).delete(String(key));
            return true;
        });
    }

    async delete(storeName, key) {
        const lockKey = `${storeName}:${key}`;
        return this._withLock(lockKey, async () => {
            this._getStore(storeName).delete(String(key));
            return true;
        });
    }

    async clear(storeName) {
        this._getStore(storeName).clear();
        return true;
    }

    async count(storeName, filterFn = null) {
        const items = await this.getAll(storeName, filterFn);
        return items.length;
    }
}

let activeDriver = null;
let customNativeConnector = null;

/**
 * Permite inyectar manualmente un conector SQLite nativo (útil en testing o mocks).
 *
 * @param {object|null} connector
 */
export function injectSQLiteConnector(connector) {
    customNativeConnector = connector;
    if (activeDriver && activeDriver instanceof SQLiteStorageDriver) {
        activeDriver.nativeConnector = connector;
        activeDriver.isInitialized = false;
        activeDriver.effectiveEngine = "uninitialized";
    }
}

/**
 * Resuelve y retorna el driver de persistencia apropiado para la plataforma en ejecución.
 *
 * @param {object} [options]
 * @param {string} [options.preferDriver] - 'sqlite' | 'indexeddb' | 'memory'
 * @param {object} [options.nativeConnector] - Instancia de conector SQLite nativo si existe
 * @param {boolean} [options.strict] - Si es true, prohíbe fallbacks silenciosos a IndexedDB en SQLite
 * @returns {IndexedDBStorageDriver | SQLiteStorageDriver | MemoryStorageDriver}
 */
export function getOfflineStorageDriver(options = {}) {
    if (activeDriver && !options.preferDriver && !options.nativeConnector && options.strict === undefined) {
        return activeDriver;
    }

    const prefer = options.preferDriver;
    const isNative = typeof Capacitor !== "undefined" && typeof Capacitor.isNativePlatform === "function" && Capacitor.isNativePlatform();

    if (prefer === "sqlite" || isNative) {
        const connector = options.nativeConnector || customNativeConnector || (isNative ? createDefaultCapacitorSqliteConnector() : null);
        activeDriver = new SQLiteStorageDriver(connector, new IndexedDBStorageDriver(), {
            strict: Boolean(options.strict),
        });
        return activeDriver;
    }

    if (prefer === "memory" || typeof window === "undefined") {
        activeDriver = new MemoryStorageDriver();
        return activeDriver;
    }

    activeDriver = new IndexedDBStorageDriver();
    return activeDriver;
}

/**
 * Inicializa formalmente el almacenamiento offline antes de montar colas o vistas.
 * En entornos nativos conecta e inicializa las tablas SQLite; en web asegura la durabilidad de IndexedDB.
 *
 * @param {object} [options]
 * @param {string} [options.preferDriver]
 * @param {object} [options.nativeConnector]
 * @param {boolean} [options.strict]
 * @param {string} [options.assertEngine] - Motor esperado ('sqlite' | 'indexeddb' | 'memory')
 * @returns {Promise<IndexedDBStorageDriver | SQLiteStorageDriver | MemoryStorageDriver>}
 */
export async function initializeOfflineStorage(options = {}) {
    const isNative = typeof Capacitor !== "undefined" && typeof Capacitor.isNativePlatform === "function" && Capacitor.isNativePlatform();
    const preferDriver = options.preferDriver || (isNative ? "sqlite" : "indexeddb");

    let connector = options.nativeConnector || customNativeConnector;
    if ((preferDriver === "sqlite" || isNative) && !connector && isNative) {
        connector = createDefaultCapacitorSqliteConnector(options.databaseName || "coordinacion_visitas_offline");
    }

    activeDriver = getOfflineStorageDriver({
        preferDriver,
        nativeConnector: connector,
        strict: Boolean(options.strict),
    });

    await activeDriver.init();

    if (options.assertEngine) {
        assertExpectedStorageEngine(options.assertEngine);
    }

    return activeDriver;
}

/**
 * Retorna el estado y diagnóstico del motor de persistencia efectivo en ejecución.
 * Permite a pruebas y telemetría detectar si una prueba en Android está degradada a IndexedDB.
 *
 * @returns {{
 *   engine: 'sqlite' | 'indexeddb' | 'memory' | 'none' | 'unknown',
 *   isNative: boolean,
 *   isFallback: boolean,
 *   connectorReady: boolean,
 *   details: string,
 *   warning: string | null,
 *   lastInitError: string | null
 * }}
 */
export function getEffectiveStorageEngine() {
    const isNative = typeof Capacitor !== "undefined" && typeof Capacitor.isNativePlatform === "function" && Capacitor.isNativePlatform();

    if (!activeDriver) {
        return {
            engine: "none",
            isNative,
            isFallback: false,
            connectorReady: false,
            details: "El driver de persistencia no ha sido instanciado.",
            warning: isNative ? "ADVERTENCIA: Plataforma nativa sin driver de persistencia instanciado." : null,
            lastInitError: null,
        };
    }

    if (activeDriver instanceof MemoryStorageDriver) {
        return {
            engine: "memory",
            isNative,
            isFallback: false,
            connectorReady: false,
            details: "Driver en memoria activo.",
            warning: isNative ? "ADVERTENCIA CRITICA: Plataforma nativa ejecutando sobre MemoryStorageDriver." : null,
            lastInitError: null,
        };
    }

    if (activeDriver instanceof SQLiteStorageDriver) {
        const engine = activeDriver.effectiveEngine;
        const isFallback = activeDriver.isFallback || engine === "indexeddb";
        const isSqlite = engine === "sqlite";

        let warning = null;
        if (isNative && isFallback) {
            warning = "ADVERTENCIA CRITICA: La aplicacion corre en plataforma nativa Android pero el motor de persistencia efectivo es IndexedDB (fallback activo). SQLite no esta operando.";
        }

        return {
            engine: isSqlite ? "sqlite" : "indexeddb",
            isNative,
            isFallback,
            connectorReady: isSqlite && Boolean(activeDriver.nativeConnector),
            details: isSqlite
                ? "Motor SQLite nativo activo con tablas y esquemas inicializados."
                : "SQLiteStorageDriver degradado a fallback IndexedDB por fallo o ausencia de conector nativo.",
            warning,
            lastInitError: activeDriver.lastInitError ? String(activeDriver.lastInitError?.message || activeDriver.lastInitError) : null,
        };
    }

    if (activeDriver instanceof IndexedDBStorageDriver) {
        return {
            engine: "indexeddb",
            isNative,
            isFallback: isNative,
            connectorReady: false,
            details: "IndexedDBStorageDriver activo.",
            warning: isNative ? "ADVERTENCIA CRITICA: Plataforma nativa Android usando IndexedDB directamente en lugar de SQLite." : null,
            lastInitError: null,
        };
    }

    return {
        engine: "unknown",
        isNative,
        isFallback: false,
        connectorReady: false,
        details: "Driver desconocido.",
        warning: null,
        lastInitError: null,
    };
}

/**
 * Valida que el motor de almacenamiento efectivo coincida con el esperado.
 * Lanza una excepción si hay discrepancia (útil en tests de Android para evitar falsos positivos con IndexedDB).
 *
 * @param {string} [expected="sqlite"]
 * @returns {object} Status del motor efectivo
 * @throws {Error} Si el motor efectivo difiere del esperado
 */
export function assertExpectedStorageEngine(expected = "sqlite") {
    const status = getEffectiveStorageEngine();
    if (status.engine !== expected || status.isFallback) {
        const extra = status.warning ? ` [${status.warning}]` : "";
        const errDetail = status.lastInitError ? ` (Causa: ${status.lastInitError})` : "";
        throw new Error(
            `Fallo de comprobacion de motor offline: se esperaba '${expected}', pero el motor efectivo es '${status.engine}' (isNative=${status.isNative}, fallback=${status.isFallback})${extra}${errDetail}.`
        );
    }
    return status;
}

/**
 * Comprobación booleana del motor efectivo sin lanzar excepción.
 *
 * @param {string} [expected="sqlite"]
 * @returns {boolean}
 */
export function isStorageEngineEffective(expected = "sqlite") {
    const status = getEffectiveStorageEngine();
    return status.engine === expected && !status.isFallback;
}

export function resetActiveStorageDriver() {
    activeDriver = null;
    customNativeConnector = null;
}

/**
 * Ejecuta una inserción o reemplazo atómico multi-store a través del driver activo.
 *
 * @param {Array<{storeName: string, value: object}>} items
 * @param {object} [options]
 * @returns {Promise<Array<object>>}
 */
export async function atomicPutBatch(items, options = {}) {
    const driver = getOfflineStorageDriver(options);
    return driver.atomicPutBatch(items);
}

export async function mutate(storeName, key, mutatorFn, options = {}) {
    const driver = getOfflineStorageDriver(options);
    return driver.mutate(storeName, key, mutatorFn);
}

export async function compareAndDelete(storeName, key, predicateFn, options = {}) {
    const driver = getOfflineStorageDriver(options);
    return driver.compareAndDelete(storeName, key, predicateFn);
}

// Exposición en runtime de ventana para depuración, testing y diagnóstico remoto
if (typeof window !== "undefined") {
    window.__OFFLINE_STORAGE__ = {
        getEffectiveStorageEngine,
        assertExpectedStorageEngine,
        isStorageEngineEffective,
        initializeOfflineStorage,
        injectSQLiteConnector,
        createDefaultCapacitorSqliteConnector,
    };
}

export { IndexedDBStorageDriver, SQLiteStorageDriver, MemoryStorageDriver };
